import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { prepareQuickViewContent } from '@theme/theme-quick-view';

class ThemePurchaseOptions extends HTMLElement {
  static nextGroup = 0;
  #radioGroup = 'theme-selling-plan-' + ++ThemePurchaseOptions.nextGroup;
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.name = this.#radioGroup;
    });
    this.addEventListener('change', this.#handleChange);
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    // Native selects can match :focus-visible after pointer clicks in Chrome.
    if (this.closest('.product-page-section[data-product-page]')) {
      document.addEventListener('keydown', this.#handleKeyboard, true);
      document.addEventListener('pointerdown', this.#handlePointer, true);
    }
    this.#sync(this.#selectedValue());
  }

  // Quick View calls this after morphing, once its native form is present.
  syncForm() {
    this.#sync(this.#selectedValue());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
    document.removeEventListener('keydown', this.#handleKeyboard, true);
    document.removeEventListener('pointerdown', this.#handlePointer, true);
  }

  #handleKeyboard = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || ['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) return;
    this.dataset.focusModality = 'keyboard';
  };

  #handlePointer = () => {
    this.dataset.focusModality = 'pointer';
  };

  #handleChange = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    this.#sync(control.matches('[data-subscription]') ? this.querySelector('[data-delivery]')?.value ?? '' : control.value);
  };

  /**
   * Replaces the selling-plan choices with the variant-specific server render.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const blockId = this.dataset.blockId;
        if (!blockId) return;

        const updatedOptions = detail.html.querySelector(
          'theme-purchase-options[data-block-id="' + CSS.escape(blockId) + '"]'
        );
        if (!(updatedOptions instanceof HTMLElement)) return;

        // Quick View keeps details links in its own action areas after the initial render.
        if (this.closest('#quick-add-dialog')) {
          prepareQuickViewContent(updatedOptions, null, this.querySelector('template[data-theme-quick-view-active-override]'));
          updatedOptions.querySelector('.theme-purchase-options__full-details')?.remove();
        }
        const selectedValue = this.#selectedValue();
        const validValues = this.#validValues(updatedOptions);
        const fallbackValue = validValues.includes('') ? '' : validValues[0] ?? '';
        this.#setSelected(validValues.includes(selectedValue) ? selectedValue : fallbackValue, updatedOptions);
        this.replaceWith(updatedOptions);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[theme-purchase-options] Event promise rejected:', error);
      });
  };

  #selectedValue() {
    const selectedRadio = this.querySelector('input[type="radio"]:checked');
    if (selectedRadio instanceof HTMLInputElement) {
      return selectedRadio.matches('[data-subscription]')
        ? this.querySelector('[data-delivery]')?.value ?? ''
        : selectedRadio.value;
    }

    const select = this.querySelector('select');
    return select instanceof HTMLSelectElement ? select.value : '';
  }

  #validValues(root = this) {
    const delivery = root.querySelector('[data-delivery]');
    if (delivery instanceof HTMLSelectElement) {
      const values = Array.from(delivery.options, (option) => option.value);
      if (root.querySelector('input[type="radio"][value=""]')) values.unshift('');
      return values;
    }
    const radios = Array.from(root.querySelectorAll('input[type="radio"]'));
    if (radios.length > 0) return radios.map((radio) => radio.value);

    const select = root.querySelector('select');
    return select instanceof HTMLSelectElement ? Array.from(select.options, (option) => option.value) : [''];
  }

  #setSelected(value, root = this) {
    root.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.checked = radio.matches('[data-subscription]') ? value !== '' : radio.value === value;
    });

    const select = root.querySelector('select');
    if (select instanceof HTMLSelectElement && (value !== '' || !select.matches('[data-delivery]'))) select.value = value;
  }

  #sync(value) {
    // Morph keeps this element connected but restores server-rendered option text.
    if (this.closest('#quick-add-dialog')) {
      this.querySelectorAll('[data-quick-view-label]').forEach((option) => {
        option.textContent = option.dataset.quickViewLabel;
      });
    }

    const product = this.closest('product-component') ?? this.parentElement;
    product?.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      // Related product cards own their purchase choice, independently of this PDP.
      if (form.closest('product-card')) return;

      let input = form.querySelector('input[name="selling_plan"]');
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.append(input);
      }

      input.value = value;
      input.disabled = value === '';
    });

    this.#setSelected(value);
    const option = this.querySelector('[data-delivery] option:checked');
    if (option instanceof HTMLOptionElement) {
      const price = this.querySelector('[data-plan-price]');
      const saving = this.querySelector('[data-plan-saving]');
      const compare = this.querySelector('[data-plan-compare]');
      if (price) price.textContent = option.dataset.price ?? '';
      if (saving instanceof HTMLElement) {
        saving.textContent = option.dataset.savingLabel ?? '';
        saving.hidden = Number(option.dataset.saving) <= 0;
      }
      if (compare instanceof HTMLElement) compare.hidden = Number(option.dataset.saving) <= 0;
    }
  }
}

if (!customElements.get('theme-purchase-options')) {
  customElements.define('theme-purchase-options', ThemePurchaseOptions);
}
